<script setup lang="ts">
import { z } from "zod";
import type { FormSubmitEvent, Form } from "#ui/types";
import { useVerifyEmail } from "~/composables/auth/useVerifyEmail";
import { useGeneratePasswordResetCode } from "~/composables/auth/useGeneratePasswordResetCode";

definePageMeta({
  layout: "auth",
});

useSeoMeta({
  title: "Forgot password",
});

const { generatePasswordResetCode, loading, error } = useGeneratePasswordResetCode();
const toast = useToast();

const ui = computed(() => {
  const container: string = "gap-y-6 flex flex-col";

  return {
    wrapper: "w-full max-w-md space-y-6",
    container,
    base: "text-center",
    title: "text-2xl text-gray-900 dark:text-white font-semibold py",
    description: "text-gray-500 dark:text-gray-400 my-4 mb-4",
  };
});

const schema = z.object({
  email: z.string().email(),
});

type Schema = z.output<typeof schema>;

const state = reactive({
  email: undefined,
});

const labels = reactive({
  title: "Request a password reset code",
  description: "We will send you a code to reset your password"
});

const form = ref<Form<Schema>>();


async function onSubmit(event: FormSubmitEvent<Schema>) {
  await generatePasswordResetCode(state.email);
  if (error.value) {
    form.value!.setErrors([
      {
        message: "Error generating password reset code",
        path: "email",
      }
    ]);
    return;
  }

  navigateTo({ path: "/reset-password", query: { email: state.email } })
}



</script>

<!-- eslint-disable vue/multiline-html-element-content-newline -->
<!-- eslint-disable vue/singleline-html-element-content-newline -->
<template>
  <UCard class="max-w-md w-full bg-white/75 dark:bg-white/5 backdrop-blur">
    <div :class="ui.wrapper">
      <div v-if="labels.title || labels.description" :class="ui.base">

        <div v-if="labels.title" :class="ui.title">
          {{ labels.title }}
        </div>

        <div v-if="labels.description" :class="ui.description">
          {{ labels.description }} or click to
          <NuxtLink to="/login" class="text-primary font-medium">log in</NuxtLink>.
        </div>

      </div>
      <div :class="ui.container">
        <UForm :schema="schema" :state="state" ref="form" class="space-y-4" @submit="onSubmit"
          :submit-button="{ trailingIcon: 'i-heroicons-arrow-right-20-solid' }">

          <UFormGroup name="email" label="Email">
            <UInput v-model="state.email" type="email" autocomplete="off" size="md" name="email"
              label="Enter your email">
            </UInput>
          </UFormGroup>

          <div>
            <UButton type="submit" block :loading="loading">
              Send code
            </UButton>
          </div>
        </UForm>
      </div>
    </div>
  </UCard>
</template>
